import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Sparkles, TrendingDown, Check } from 'lucide-react';
import { billingApi } from '../lib/billingApi';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../lib/i18n';

const STR = {
  fr: {
    close_aria:      'Fermer',
    eyebrow:         'Offre fidélité',
    headline_a:      'Économise 20 %',
    headline_b:      "en passant à l'annuel",
    body_pre:        'Tu fais partie de SoumissionPro depuis plus de ',
    body_30days:     '30 jours',
    body_mid:        '. Merci de ta confiance ! 💛',
    body_switch_pre: "Passe à l'",
    body_switch_emph: 'abonnement annuel',
    body_switch_mid: " et économise l'équivalent de ",
    body_switch_emph2: 'plus de 2 mois',
    body_switch_post: ' chaque année.',
    current_plan:    'Plan actuel',
    monthly:         'Mensuel',
    monthly_price:   '29,99 $/mois',
    monthly_year:    '≈ 359,88 $/an',
    in_annual:       'En annuel',
    annual:          'Annuel',
    annual_price:    '23,99 $/mois',
    annual_year:     '287,90 $/an',
    savings:         '💰 Économise 71,98 $ par année',
    benefit1:        'Toutes les mêmes fonctionnalités',
    benefit2:        'Aucun changement pour ton équipe',
    benefit3:        'Annule à tout moment depuis ton profil',
    cta:             "Passer à l'annuel et économiser →",
    later:           'Plus tard, merci',
  },
  en: {
    close_aria:      'Close',
    eyebrow:         'Loyalty offer',
    headline_a:      'Save 20%',
    headline_b:      'by switching to annual',
    body_pre:        "You've been with SoumissionPro for over ",
    body_30days:     '30 days',
    body_mid:        '. Thank you for your trust! 💛',
    body_switch_pre: 'Switch to the ',
    body_switch_emph: 'annual subscription',
    body_switch_mid: ' and save the equivalent of ',
    body_switch_emph2: 'more than 2 months',
    body_switch_post: ' every year.',
    current_plan:    'Current plan',
    monthly:         'Monthly',
    monthly_price:   '$29.99/month',
    monthly_year:    '≈ $359.88/year',
    in_annual:       'In annual',
    annual:          'Annual',
    annual_price:    '$23.99/month',
    annual_year:     '$287.90/year',
    savings:         '💰 Save $71.98 per year',
    benefit1:        'All the same features',
    benefit2:        'Nothing changes for your team',
    benefit3:        'Cancel anytime from your profile',
    cta:             'Switch to annual and save →',
    later:           'Later, thanks',
  },
};

/**
 * AnnualUpgradePopup
 * ------------------
 * Shows a gentle, non-intrusive upgrade nudge to users who have been on the
 * MONTHLY plan for 30+ days. The annual plan is ~20% cheaper, so this is a
 * win/win conversion lever (more cash up front for SoumissionPro, savings
 * for the customer).
 *
 * Tracking is done entirely client-side via localStorage:
 *  - `monthly_first_seen:<userId>` — first time we observed plan === 'monthly'
 *  - `annual_popup_dismissed:<userId>` — last time the user dismissed it
 *
 * Re-shows every 14 days after dismissal so we don't nag.
 */
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const COOLDOWN_AFTER_DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

function shouldShowPopup(userId, plan) {
  if (!userId || plan !== 'monthly') return false;
  const firstSeenKey = `soumissionpro:monthly_first_seen:${userId}`;
  const dismissedKey = `soumissionpro:annual_popup_dismissed:${userId}`;
  let firstSeen;
  try {
    firstSeen = window.localStorage.getItem(firstSeenKey);
    if (!firstSeen) {
      window.localStorage.setItem(firstSeenKey, Date.now().toString());
      return false; // First detection — start the timer.
    }
  } catch {
    return false;
  }
  const ageMs = Date.now() - parseInt(firstSeen, 10);
  if (ageMs < THIRTY_DAYS_MS) return false;
  // Cooldown
  try {
    const dismissed = window.localStorage.getItem(dismissedKey);
    if (dismissed) {
      const sinceDismiss = Date.now() - parseInt(dismissed, 10);
      if (sinceDismiss < COOLDOWN_AFTER_DISMISS_MS) return false;
    }
  } catch {
    // ignore
  }
  return true;
}

export function AnnualUpgradePopup() {
  const { user } = useAuth();
  const { lang } = useT();
  const T = STR[lang === 'en' ? 'en' : 'fr'];
  const [show, setShow] = useState(false);
  const [plan, setPlan] = useState(null);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) return;
    billingApi
      .status()
      .then((s) => {
        if (cancelled) return;
        setPlan(s?.plan || null);
        // Slight delay so the page settles before the modal appears.
        if (shouldShowPopup(user.id, s?.plan)) {
          setTimeout(() => {
            if (!cancelled) setShow(true);
          }, 800);
        }
      })
      .catch(() => { /* silent — not critical */ });
    return () => { cancelled = true; };
  }, [user]);

  const handleDismiss = () => {
    if (!user?.id) return;
    try {
      window.localStorage.setItem(
        `soumissionpro:annual_popup_dismissed:${user.id}`,
        Date.now().toString()
      );
    } catch {
      // ignore
    }
    setClosing(true);
    setTimeout(() => setShow(false), 200);
  };

  if (!show || plan !== 'monthly') return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{
        backgroundColor: 'rgba(90, 22, 32, 0.55)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        opacity: closing ? 0 : 1,
        transition: 'opacity 200ms ease',
      }}
      onClick={handleDismiss}
      data-testid="annual-upgrade-popup"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          backgroundColor: '#FFFFFF',
          boxShadow: '0 20px 60px rgba(122, 31, 43, 0.35)',
          transform: closing ? 'translateY(20px)' : 'translateY(0)',
          transition: 'transform 200ms ease',
        }}
      >
        {/* Close X */}
        <button
          onClick={handleDismiss}
          aria-label={T.close_aria}
          data-testid="annual-popup-close-btn"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center transition hover:bg-white/20"
          style={{ color: '#FFFFFF' }}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Top hero band */}
        <div
          className="px-7 pt-8 pb-7 text-center text-white"
          style={{
            background: 'linear-gradient(135deg, #7A1F2B 0%, #5A1620 100%)',
          }}
        >
          <div
            className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ backgroundColor: '#C9A961' }}
          >
            <Sparkles className="w-8 h-8" style={{ color: '#5A1620' }} />
          </div>
          <div className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: '#C9A961' }}>
            {T.eyebrow}
          </div>
          <h2
            className="text-2xl font-bold leading-tight"
            style={{ letterSpacing: '-0.5px' }}
          >
            {T.headline_a}<br />{T.headline_b}
          </h2>
        </div>

        {/* Body */}
        <div className="px-7 py-6">
          <p className="text-sm leading-relaxed mb-5" style={{ color: '#4A5568' }}>
            {T.body_pre}<strong style={{ color: '#5A1620' }}>{T.body_30days}</strong>{T.body_mid}
            <br /><br />
            {T.body_switch_pre}<strong style={{ color: '#7A1F2B' }}>{T.body_switch_emph}</strong>{T.body_switch_mid}<strong style={{ color: '#7A1F2B' }}>{T.body_switch_emph2}</strong>{T.body_switch_post}
          </p>

          {/* Price comparison */}
          <div
            className="rounded-2xl p-4 mb-5"
            style={{ backgroundColor: '#FAF7F2', border: '1px solid #E5DCC9' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#9CA3AF' }}>
                  {T.current_plan}
                </div>
                <div className="text-sm font-semibold" style={{ color: '#6B7280' }}>
                  {T.monthly}
                </div>
                <div className="text-lg font-bold" style={{ color: '#6B7280', textDecoration: 'line-through' }}>
                  {T.monthly_price}
                </div>
                <div className="text-xs" style={{ color: '#6B7280' }}>
                  {T.monthly_year}
                </div>
              </div>
              <TrendingDown className="w-6 h-6" style={{ color: '#C9A961' }} />
              <div className="text-right">
                <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#7A1F2B' }}>
                  {T.in_annual}
                </div>
                <div className="text-sm font-semibold" style={{ color: '#5A1620' }}>
                  {T.annual}
                </div>
                <div className="text-lg font-black" style={{ color: '#5A1620' }}>
                  {T.annual_price}
                </div>
                <div className="text-xs font-semibold" style={{ color: '#16A34A' }}>
                  {T.annual_year}
                </div>
              </div>
            </div>
            <div
              className="rounded-lg px-3 py-2 text-center text-xs font-bold"
              style={{ backgroundColor: '#16A34A', color: '#FFFFFF' }}
              data-testid="annual-popup-savings"
            >
              {T.savings}
            </div>
          </div>

          {/* Benefits */}
          <ul className="space-y-2 mb-6">
            <li className="flex items-start gap-2 text-sm" style={{ color: '#374151' }}>
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
              <span>{T.benefit1}</span>
            </li>
            <li className="flex items-start gap-2 text-sm" style={{ color: '#374151' }}>
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
              <span>{T.benefit2}</span>
            </li>
            <li className="flex items-start gap-2 text-sm" style={{ color: '#374151' }}>
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
              <span>{T.benefit3}</span>
            </li>
          </ul>

          {/* CTA */}
          <Link
            to="/pricing?focus=annual"
            onClick={handleDismiss}
            data-testid="annual-popup-upgrade-btn"
            className="block w-full text-center py-3.5 rounded-2xl font-bold text-white text-base"
            style={{
              backgroundColor: '#7A1F2B',
              boxShadow: '0 4px 14px rgba(122, 31, 43, 0.3)',
            }}
          >
            {T.cta}
          </Link>

          <button
            onClick={handleDismiss}
            data-testid="annual-popup-later-btn"
            className="block w-full text-center mt-2 py-2.5 text-sm font-semibold transition hover:underline"
            style={{ color: '#6B7280' }}
          >
            {T.later}
          </button>
        </div>
      </div>
    </div>
  );
}
